/* eslint-disable react-hooks/exhaustive-deps */
import { FC, KeyboardEvent, ReactNode, RefObject, SyntheticEvent, useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useConditionalEffect, usePrevious } from 'react-delta';

import ELEMENT from 'common/element';
import { getCursorPosition, setCursorPosition } from 'service/browser';

import { ExpandCollapseButton } from './ExpandCollapseButton';
import './Input.scss';

const CSS_UNSET = 'unset';

export enum ExpandedState {
  EXPANDED = 'EXPANDED',
  COLLAPSED = 'COLLAPSED',
  // Unset is before the user has clicked the button. Input expands/collapses with text input
  UNSET = 'UNSET',
}

const setTextAreaHeight = (textArea: HTMLTextAreaElement, height: string) => {
  const parent = textArea.parentNode as HTMLElement;
  textArea.style.setProperty('--input-height', height);
  parent.style.setProperty('--input-height', height);
};

interface ExpandProps {
  enabled: boolean; // whether to enable expand/collapse functionality
  minHeight: string;
}

type ResizableTextAreaProps = {
  label?: string | ReactNode;
  value?: string;
  onChange?: (event: SyntheticEvent) => void;
  id?: string;
  groupId?: string;
  expandProps?: ExpandProps;
  inputClassName?: string;
  labelClassName?: string;
  labelTextClassName?: string;
  errorTextClassName?: string;
  validTextClassName?: string;
  disabled?: boolean;
  placeholder?: string;
  isInvalid?: boolean;
  isValid?: boolean;
  name?: string;
  minRows?: number;
  focused?: boolean;
  onPressEnter?: () => void;
  errorMessage?: string;
  validMessage?: string;
  testId?: ELEMENT;
  inputRef?: RefObject<HTMLTextAreaElement>;
  fullHeight?: boolean;
  useShiftOnEnter?: boolean;
  useMetaOnEnter?: boolean;
};

/**
 * This is a resizable  textarea. It starts with a size based on 'minRows' and can grow indefinitely based on content.
 *
 * @prop label                 label text for the input
 * @prop value                 starting value for the input field
 * @prop onChange              function to call when value changes
 * @prop id                    DOM ID for the input
 * @prop groupId               DOM ID for the form group
 * @prop expandProps           settings for expand/collapse functionality
 * @prop inputClassName        classname to style input HTML element
 * @prop labelClassName        classname to style label HTML element
 * @prop labelTextClassName    classname to style span with label text
 * @prop errorTextClassName    classname to style error message
 * @prop validTextClassName    classname to style valid message
 * @prop disabled              if true, input is disabled
 * @prop placeholder           placeholder for the input field
 * @prop isInvalid             Change styles if input not validated
 * @prop isValid               Change styles if input is validated
 * @prop name                  DOM name for the input
 * @prop minRows               determines minimal height of text area in rows
 * @prop focused               determines whether input needs to be focused when rendered
 * @prop onPressEnter          function to call when Enter has been typed
 * @prop errorMessage          error message for the validation input
 * @prop validMessage          message for the validation input
 * @prop testId                identifying string to use for testing (applied to textarea)
 * @prop inputRef              ref for the TEXTAREA
 * @prop useShiftOnEnter       if true, Shift plus Enter triggers onPressEnter action
 * @prop useMetaOnEnter        if true, Cmd or Win plus Enter triggers onPressEnter action
 */

const ResizableTextArea: FC<ResizableTextAreaProps> = ({
  label,
  value = '',
  onChange,
  id,
  expandProps,
  groupId,
  inputClassName = '',
  labelClassName = '',
  labelTextClassName = '',
  errorTextClassName = '',
  validTextClassName = '',
  disabled = false,
  placeholder = '',
  isInvalid,
  isValid,
  name = '',
  minRows = 2,
  focused,
  onPressEnter,
  errorMessage,
  validMessage,
  testId,
  inputRef,
  fullHeight,
  useShiftOnEnter = false,
  useMetaOnEnter = false,
}: ResizableTextAreaProps) => {
  const [cursorPos, setCursorPos] = useState(-1);
  const localRef = useRef<HTMLTextAreaElement>(null);
  const textAreaRef = inputRef || localRef;
  const [expandedState, setExpandedState] = useState<ExpandedState>(ExpandedState.UNSET);
  const onExpand = () => {
    setExpandedState(ExpandedState.EXPANDED);
    if (textAreaRef.current) {
      setTextAreaHeight(textAreaRef.current, CSS_UNSET);
      textAreaRef.current.focus();
    }
  };
  const onCollapse = () => {
    setExpandedState(ExpandedState.COLLAPSED);
    if (textAreaRef.current) {
      setTextAreaHeight(textAreaRef.current, expandProps?.minHeight ?? CSS_UNSET);
      textAreaRef.current.focus();
    }
  };

  useEffect(() => {
    if (focused && textAreaRef.current) {
      textAreaRef.current.focus();
    }
  }, []);

  const prevValue = usePrevious(value);
  const valueChanged = value !== prevValue;
  useConditionalEffect(() => {
    const textArea = textAreaRef?.current;
    if (textArea) {
      // restore cursor position; -1 means end of input
      setCursorPosition(textArea, cursorPos);
      const parent = textArea.parentNode as HTMLElement;
      if (parent && parent.dataset) {
        parent.dataset.value = textArea.value;
      }
    }
  }, valueChanged);

  useConditionalEffect(() => {
    if (textAreaRef.current && expandProps?.enabled) {
      // unset expanded state if the input returns to the min height
      const textAreaHeight = parseInt(getComputedStyle(textAreaRef.current).height);
      const minTextAreaHeight = parseInt(expandProps.minHeight);
      if (textAreaHeight <= minTextAreaHeight) {
        setExpandedState(ExpandedState.UNSET);
      }
    }
  }, expandedState === ExpandedState.EXPANDED && valueChanged);

  const onChangeHandler = (event: SyntheticEvent): void => {
    const textArea = textAreaRef?.current;
    if (textArea) {
      // remember cursor position in case element gets re-rendered
      let cursor = getCursorPosition(textArea);
      if (cursor === textArea.value.length) {
        cursor = -1;
      }
      setCursorPos(cursor);
    }
    if (onChange) {
      onChange(event);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (
      event.key === 'Enter' &&
      onPressEnter &&
      ((!useShiftOnEnter && !useMetaOnEnter) ||
        (useShiftOnEnter && event.shiftKey) ||
        (useMetaOnEnter && event.metaKey))
    ) {
      event.preventDefault();
      onPressEnter();
    }
  };

  const labelEl = label ? <Form.Label className={labelTextClassName}>{label}</Form.Label> : null;

  const sizeClassName = fullHeight ? 'ResizableTextArea__Group--FullSize' : '';

  return (
    <Form.Group className={`ResizableTextArea__Group ${labelClassName} ${sizeClassName}`} id={groupId}>
      {labelEl}
      <Form.Control
        as="textarea"
        rows={minRows}
        ref={textAreaRef}
        placeholder={placeholder}
        value={value}
        onChange={onChangeHandler}
        disabled={disabled}
        id={id}
        name={name}
        isInvalid={isInvalid}
        isValid={isValid}
        className={`ResizableTextArea__TextArea ${inputClassName}`}
        onKeyDown={handleKeyDown}
        data-test-id={testId}
        spellCheck={false}
      />
      {expandProps?.enabled && (
        <ExpandCollapseButton
          expandedState={expandedState}
          onExpand={onExpand}
          onCollapse={onCollapse}
          textAreaRef={textAreaRef}
          minHeight={expandProps.minHeight}
        />
      )}
      <Form.Control.Feedback type="invalid" className={errorTextClassName}>
        {errorMessage}
      </Form.Control.Feedback>
      <Form.Control.Feedback type="valid" className={validTextClassName}>
        {validMessage}
      </Form.Control.Feedback>
    </Form.Group>
  );
};

export default ResizableTextArea;
